import { Building2Icon, CalendarDaysIcon, ChevronDown, ClipboardListIcon, Dots, Loading2, Mail, Pencil, PhoneIcon } from 'assets/icons';
import Badge from 'components/badge/badge';
import Button from 'components/button';
import { cardIconButtonClass } from 'components/card/PanelCard';
import MenuButton from 'components/DropdownMenu/MenuButton';
import CompactEmptyState from 'components/EmptyState/CompactEmptyState';
import IconTooltip from 'components/iconTooltip/IconTooltip';
import useModal from 'components/modal/useModal';
import RecordActivity from 'components/record/activity/RecordActivity';
import { buildActivity } from 'components/record/activity/activityItems';
import CompanyContacts from 'components/record/CompanyContacts';
import ExchangeList from 'components/record/ExchangeList';
import KpiTiles from 'components/record/KpiTiles';
import NextActionCard from 'components/record/NextActionCard';
import QuickActions from 'components/record/QuickActions';
import RecordFields from 'components/record/RecordFields';
import RecordIdentity, { RecordIcon } from 'components/record/RecordIdentity';
import RecordLayout, { RecordSection, RecordTabs } from 'components/record/RecordLayout';
import RecordMissions from 'components/record/RecordMissions';
import ResultsCard from 'components/record/ResultsCard';
import SlideOverCompanyInvoicesTab from 'components/SlideOver/dataSlideOver/data/slideOverCompany/SlideOverCompanyInvoicesTab';
import SlideOverCompanyRemunerationConditions from 'components/SlideOver/dataSlideOver/data/slideOverCompany/slideOverCompanyRemunerationConditions';
import useSlideOver from 'components/SlideOver/useSlideOver';
import { useOnChange } from 'hooks/useChangeSignal';
import UseContext from 'hooks/useContext';
import useCompanyFilterOptions from 'hooks/useCompanyFilterOptions';
import useCrmContext from 'hooks/useCrmContext';
import Usefetchdata from 'hooks/usefetchdata';
import useRecordChanged from 'hooks/useRecordChanged';
import useRecordPanels from 'hooks/useRecordPanels';
import useTaskPanels from 'hooks/useTaskPanels';
import useTasksChanged from 'hooks/useTasksChanged';
import useText from 'hooks/useText';
import { hidesProspection } from 'helper/roles';
import { isoDay } from 'helper/timeLabels';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import ApiDatabase from 'server';
import crmApi from 'server/crm/crmApi';
import { departmentLabel, dateLabel } from './companiesItems';
import { STAGE_BADGES, exchangeWhen, exchangesOf, formatSiret, fullName, kpiTiles, nextActionView, wholeEuros } from './recordItems';

const TABS = ['overview', 'activity', 'contacts', 'missions', 'billing', 'pay'];

/**
 * The page of a company, as on the prototype: on the left who it is, its
 * actions, what comes next and what we know of it; on the right its numbers
 * and its tabs (overview, activity, contacts, missions, billing, pay
 * conditions). Jobbiz gives the company, its members, its missions, its
 * invoices and its pay conditions; the sales side comes from server/crm
 * (crmRecordApi.js) until the api has it. `?onglet=` opens a tab.
 */
const CompanyPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { text } = useText('record');
  const { text: activityText } = useText('recordActivity');
  const { call, ready } = useCrmContext();
  const { userData } = Usefetchdata();
  const { setObjID, setModalData } = UseContext();
  const { toggleSlideOver } = useSlideOver();
  const { toggle } = useModal();
  const { openRecordForm } = useRecordPanels();
  const { openTask, openTaskForm } = useTaskPanels();
  const { categories } = useCompanyFilterOptions();
  const canSeeInvoices = Boolean(userData?.accessInvoice);
  const prospects = !hidesProspection(userData?.role);
  const tabs = TABS.filter((tab) => tab !== 'billing' || canSeeInvoices);
  const [record, setRecord] = useState(null);
  const [failed, setFailed] = useState(null);
  const [tab, setTab] = useState(() => (tabs.includes(params.get('onglet')) ? params.get('onglet') : 'overview'));
  const [missionsView, setMissionsView] = useState(params.get('vue') === 'interimaires' ? 'workers' : 'missions');
  const [more, setMore] = useState(false);
  const [companyInfo, setCompanyInfo] = useState(null);

  const load = useCallback(
    () =>
      call(crmApi.getCompanyRecord, { companyId: id, canSeeInvoices }).then(
        (next) => {
          setRecord(next);
          setCompanyInfo(next.company);
          setFailed(null);
        },
        (error) => setFailed(error.message),
      ),
    [call, id, canSeeInvoices],
  );
  useEffect(() => {
    if (ready) load();
  }, [load, ready]);
  useRecordChanged(load);
  useTasksChanged(load);
  // A member added or invited from the admin's modal.
  useOnChange('employee_created', load);

  const team = useMemo(() => record?.team ?? [], [record]);
  const nameOf = useCallback(
    (personId) => {
      const person = team.find((member) => member.id === personId);
      return person ? fullName(person) : text('someone');
    },
    [team, text],
  );
  const now = new Date();
  const items = useMemo(
    () => (record ? buildActivity(record.history, { now: new Date(), text: activityText, nameOf }) : []),
    [record, activityText, nameOf],
  );

  if (failed) {
    return (
      <RecordLayout side={null}>
        <CompactEmptyState
          glyph="searchX"
          title={text('companyMissing')}
          description={failed}
          action={{ label: text('backCompanies'), onClick: () => navigate('/company') }}
        />
      </RecordLayout>
    );
  }
  if (!record) {
    return (
      <RecordLayout side={null}>
        <div className="flex justify-center p-8" aria-busy="true">
          <span className="animate-spin">
            <Loading2 wh={24} color="#FF9600" />
          </span>
        </div>
      </RecordLayout>
    );
  }

  const { company, crm, group, members } = record;
  const name = company.name_commercial || company.reason_social || '';
  const contacts = members.map((member) => ({ id: member.id, name: fullName(member), email: member.email, phone: member.phone }));
  const openForm = (kind, extra = {}) => openRecordForm(kind, { companyId: id, recordName: name, contacts, ...extra });
  const openCompanyPanel = () => {
    setObjID(id);
    toggleSlideOver('get_company');
  };
  const comingSoon = () => toast.info(text('comingSoon'));
  const setStage = (stage) =>
    call(crmApi.setCompanyStage, { companyId: id, stage }).then(
      () => {
        toast.success(text(stage === 'client' ? 'stageClient' : 'stageProspect'));
        load();
      },
      (error) => toast.error(error.message),
    );
  const addMember = (prefill = null) => {
    setModalData(prefill ? { idCompany: id, origin: 'hubspot', hubspotContactId: prefill.hubspotId, prefill: { email: prefill.email, firstName: prefill.firstname, lastName: prefill.lastname, phone: prefill.phone } } : { idCompany: id });
    toggle('add_company_member');
  };
  const resend = (person) =>
    ApiDatabase.postSendMailLinkCompanyEmployee(
      { idCompany: id, idEmployee: person.id },
      () => toast.success(text('resent', { email: person.email })),
      (error) => toast.error(error?.response?.data?.message ?? error.message),
    );
  const add = (kind) => {
    if (kind === 'task') openTaskForm({ defaults: { type: 'todo', contact: name } });
    else openForm(kind === 'all' ? 'note' : kind === 'event' ? 'event' : kind);
  };
  const openItem = (ref) => {
    if (ref.kind === 'task') openTask(ref.id);
    if (ref.kind === 'client') {
      setMissionsView('missions');
      setTab(ref.tab);
    }
  };

  // What comes next, and the two buttons that act on it.
  const firstContact = contacts[0]?.name ?? '';
  const next = nextActionView(crm.nextAction, { text, contactName: firstContact, now });
  let nextButtons;
  if (!next) {
    nextButtons = {
      primary: { label: text('planTask'), onClick: () => add('task') },
      secondary: { label: text('planMeeting'), onClick: () => openForm('event') },
    };
  } else if (next.kind === 'task') {
    nextButtons = {
      primary: { label: text('openTask'), onClick: () => openTask(record.history.nextTaskId) },
      secondary: { label: text('postpone'), onClick: () => openTaskForm({ taskId: record.history.nextTaskId }) },
    };
  } else if (next.kind === 'callback') {
    // A callback belongs to the prospecting call list.
    nextButtons = prospects
      ? { primary: { label: text('call'), onClick: () => openForm('call', { contactName: firstContact }) } }
      : {};
  } else {
    nextButtons = {
      primary: { label: text('outcome'), onClick: () => openForm('note', { contactName: firstContact, outcome: true }) },
      secondary: { label: text('postpone'), onClick: () => openForm('event', { contactName: firstContact }) },
    };
  }

  const category = categories.find((item) => String(item.id) === String(company.pennylane_category_id));
  const address = [company.address?.street, company.address?.postcode, company.address?.city].filter(Boolean).join(', ');
  const created = record.summary?.createdAt ? dateLabel(isoDay(new Date(record.summary.createdAt)), now) : '';
  const fields = [
    { label: text('fieldLegalName'), value: company.reason_social },
    {
      label: text('fieldGroup'),
      value: group && (
        <button type="button" onClick={() => navigate(`/company/group/${group.id}`)} className="text-left hover:underline">
          {group.name}
        </button>
      ),
    },
    { label: text('fieldAddress'), value: address || departmentLabel(record.summary?.department) },
    { label: text('fieldSiret'), value: company.siret ? formatSiret(company.siret) : '' },
    { label: text('fieldSupplier'), value: company.companySupplier?.name ?? text('noSupplier') },
    { label: text('fieldApe'), value: company.code_ape },
    { label: text('fieldLegalForm'), value: company.status },
    { label: text('fieldStaff'), value: company.staff ? text('staff', { count: company.staff }) : '' },
    { label: text('fieldCapital'), value: company.capital ? wholeEuros(company.capital) : '' },
    {
      label: text('fieldAgreement'),
      value: company.collectiveAgreement?.id ? `${company.collectiveAgreement.name ?? 'IDCC'} (IDCC ${company.collectiveAgreement.id})` : '',
    },
    { label: text('fieldCategory'), value: category?.label },
    {
      label: text(crm.stage === 'client' ? 'fieldClientSince' : 'fieldCreated'),
      value: crm.stage === 'client' && crm.clientSince ? dateLabel(crm.clientSince, now) : created,
    },
    { label: text('fieldCommercial'), value: nameOf(crm.commercialId) },
  ];

  const { upcoming, recent } = exchangesOf(items);
  const exchange = (item) => ({
    key: item.key,
    title: item.title,
    detail: item.body,
    when: exchangeWhen(item),
    onOpen: item.ref ? () => openItem(item.ref) : undefined,
  });

  return (
    <RecordLayout
      side={
        <>
          <RecordIdentity
            back={{ label: text('backCompanies'), onClick: () => navigate('/company') }}
            menu={
              <MenuButton
                trigger={(toggleMenu) => (
                  <Button color="SECONDARY" size="SM" onClick={toggleMenu}>
                    <span>{text('actions')}</span>
                    <ChevronDown wh={18} color="currentColor" />
                  </Button>
                )}
                items={[
                  { label: text('editCompany'), onClick: openCompanyPanel },
                  crm.stage !== 'client' && { label: text('toClient'), onClick: () => setStage('client') },
                  crm.stage !== 'lost' && { label: text('markLost'), onClick: () => openForm('lost') },
                  crm.stage === 'lost' && { label: text('toProspect'), onClick: () => setStage('prospect') },
                  prospects && { label: text('addToCalls'), onClick: comingSoon },
                ]}
              />
            }
            picture={<RecordIcon icon={Building2Icon} />}
            title={<span className="uppercase">{name}</span>}
            badge={<Badge type={STAGE_BADGES[crm.stage]}>{text(`stage_${crm.stage}`)}</Badge>}
            subtitle={[company.id_bestt && text('number', { number: company.id_bestt }), group && text('inGroup', { name: group.name })]
              .filter(Boolean)
              .join(' · ')}
          >
            <QuickActions
              actions={[
                { key: 'note', label: text('actionNote'), icon: Pencil, onClick: () => openForm('note') },
                { key: 'email', label: text('actionEmail'), icon: Mail, onClick: () => openForm('email') },
                { key: 'call', label: text('actionCall'), icon: PhoneIcon, onClick: () => openForm('call') },
                { key: 'task', label: text('actionTask'), icon: ClipboardListIcon, onClick: () => add('task') },
                { key: 'event', label: text('actionMeeting'), icon: CalendarDaysIcon, onClick: () => openForm('event') },
              ]}
              moreLabel={text('actionMore')}
              moreIcon={Dots}
              more={[
                { label: text('addContact'), onClick: () => addMember() },
                prospects && { label: text('addToCalls'), onClick: comingSoon },
              ].filter(Boolean)}
            />
          </RecordIdentity>
          <NextActionCard
            label={text('nextAction')}
            title={next?.title ?? text('noNextAction')}
            detail={next?.detail}
            {...nextButtons}
          />
          <RecordSection className="ls:flex ls:min-h-0 ls:flex-1 ls:flex-col">
            <div className="flex items-center justify-between">
              <p className="ft-lg font-semibold text-gray-900">{text('about')}</p>
              <IconTooltip label={text('editCompany')}>
                <button type="button" aria-label={text('editCompany')} onClick={openCompanyPanel} className={cardIconButtonClass}>
                  <Pencil wh={18} color="#374151" />
                </button>
              </IconTooltip>
            </div>
            <div className="-mx-4 mt-2 px-4 ls:min-h-0 ls:flex-1 ls:overflow-y-auto">
              <RecordFields
                fields={fields}
                shown={6}
                more={more}
                onMore={() => setMore((current) => !current)}
                moreLabel={text('seeMore')}
                lessLabel={text('seeLess')}
              />
            </div>
          </RecordSection>
        </>
      }
    >
      <KpiTiles tiles={kpiTiles({ ...record.kpis, stage: crm.stage }, text)} />
      <RecordTabs
        tabs={tabs.map((one) => ({ id: one, label: text(`tab_${one}`) }))}
        activeTab={tab}
        onChange={(next) => {
          setMissionsView('missions');
          setTab(next);
        }}
      />
      {tab === 'overview' && (
        <div className="flex flex-col gap-4 pb-6">
          <ResultsCard
            results={record.results}
            since={text('since', { day: dateLabel(isoDay(new Date(record.results.since)), now) })}
          />
          <div className="grid grid-cols-1 gap-4 tab:grid-cols-2">
            <ExchangeList
              title={text('upcoming')}
              items={upcoming.map(exchange)}
              empty={{ glyph: 'calendarX', title: text('upcomingEmptyTitle'), description: text('upcomingEmptyText') }}
            />
            <ExchangeList
              title={text('recent')}
              items={recent.map(exchange)}
              empty={{ glyph: 'inbox', title: text('recentEmptyTitle'), description: text('recentEmptyText') }}
            />
          </div>
        </div>
      )}
      {tab === 'activity' && <RecordActivity items={items} onAdd={add} onOpen={openItem} />}
      {tab === 'contacts' && (
        <CompanyContacts
          members={members}
          hubspot={record.hubspot}
          group={group}
          groupContacts={record.groupContacts}
          onOpen={(person) => navigate(`/company/${id}/contacts/${person.id}`)}
          onAdd={() => addMember()}
          onInvite={addMember}
          onResend={resend}
          onOpenGroup={() => navigate(`/company/group/${group.id}`)}
          onOpenGroupContact={(person) => navigate(`/company/group/${group.id}/contacts/${person.id}`)}
        />
      )}
      {tab === 'missions' && (
        <RecordMissions key={missionsView} missions={record.missions.missions} tempWorkers={record.missions.tempWorkers} initial={missionsView} />
      )}
      {tab === 'billing' && canSeeInvoices && (
        <div className="flex flex-col gap-4 pb-6">
          <SlideOverCompanyInvoicesTab companyInfo={company} userData={userData} />
        </div>
      )}
      {tab === 'pay' && companyInfo && (
        <div className="flex flex-col gap-4 pb-6">
          <SlideOverCompanyRemunerationConditions
            companyInfo={companyInfo}
            setCompanyInfo={setCompanyInfo}
            setIsShowCoeff={openCompanyPanel}
            setIsCoeffSend={() => {}}
            onSaved={load}
          />
        </div>
      )}
    </RecordLayout>
  );
};

export default CompanyPage;
